import type { Dayjs } from "dayjs";
import { SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS } from "@common/utils/campaignBudgetMeta";
import {
  snapPickerDayjsToScheduledPhaseDayStartWallMinute,
  snapWallMinuteToMetaBudgetUtc,
} from "./metaBudgetScheduleWallSnap";
import {
  resolveEventWallTimezone,
  scheduleInputToEventWallMinute,
} from "./eventWallMinuteStrings";
import { wallMinuteToDayjsInEventZone } from "./wallMinutePickerDayjs";

const MIN_PHASE_MIN = SCHEDULED_BUDGET_PHASE_MIN_DURATION_HOURS * 60;

/** Next local midnight in `eventTz` that is still on or after `instant`. */
function nextLocalMidnightOnOrAfter(instant: Dayjs, eventTz: string): Dayjs {
  const z = instant.tz(eventTz);
  const startOfDay = z.startOf("day");
  return startOfDay.isBefore(z) ? startOfDay.add(1, "day") : startOfDay;
}

function bothPhasesMeetMin(d0: Dayjs, dMid: Dayjs, d1: Dayjs): boolean {
  return (
    dMid.isAfter(d0) &&
    d1.isAfter(dMid) &&
    dMid.diff(d0, "minute") >= MIN_PHASE_MIN &&
    d1.diff(dMid, "minute") >= MIN_PHASE_MIN
  );
}

/**
 * Default `time_start` / `time_end` for the **first** scheduled budget phase when switching to
 * **Scheduled**: prefer campaign **schedule** (guided wall `start_at` / `end_at`) when both are
 * set, otherwise **flight** ISO bounds (e.g. Ads Engage edit modal from ad set schedules).
 */
export function initialScheduledPhaseWallRange(params: {
  eventTimezone?: string;
  scheduleStartWall?: string;
  scheduleEndWall?: string;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
}): { time_start: string; time_end: string } {
  const tz = resolveEventWallTimezone(params.eventTimezone);
  const pair = (a: string, b: string) => {
    const t0 = scheduleInputToEventWallMinute(a.trim(), tz);
    const t1 = scheduleInputToEventWallMinute(b.trim(), tz);
    return t0 && t1 ? { time_start: t0, time_end: t1 } : null;
  };
  const ss = params.scheduleStartWall?.trim() ?? "";
  const se = params.scheduleEndWall?.trim() ?? "";
  if (ss && se) {
    const out = pair(ss, se);
    if (out) return out;
  }
  const fs = params.flightStartIso?.trim() ?? "";
  const fe = params.flightEndIso?.trim() ?? "";
  if (fs && fe) {
    const out = pair(fs, fe);
    if (out) return out;
  }
  return { time_start: "", time_end: "" };
}

export type ScheduledPhaseWallRow = {
  time_start: string;
  time_end: string;
};

const emptyPair: [ScheduledPhaseWallRow, ScheduledPhaseWallRow] = [
  { time_start: "", time_end: "" },
  { time_start: "", time_end: "" },
];

/**
 * When switching to **Scheduled**, seed two phases on a **local calendar midnight** so each side
 * is at least Meta’s minimum length. If the flight is too short or no midnight works, returns
 * empty rows (user picks dates).
 */
export function initialTwoScheduledPhaseWallRows(params: {
  eventTimezone?: string;
  scheduleStartWall?: string;
  scheduleEndWall?: string;
  flightStartIso?: string | null;
  flightEndIso?: string | null;
}): [ScheduledPhaseWallRow, ScheduledPhaseWallRow] {
  const tz = resolveEventWallTimezone(params.eventTimezone);
  const full = initialScheduledPhaseWallRange(params);
  if (!full.time_start?.trim() || !full.time_end?.trim()) {
    return emptyPair;
  }

  const startWall = snapWallMinuteToMetaBudgetUtc(full.time_start, tz, "start");
  const endWall = snapWallMinuteToMetaBudgetUtc(full.time_end, tz, "end");
  const d0 = wallMinuteToDayjsInEventZone(startWall, tz);
  const d1 = wallMinuteToDayjsInEventZone(endWall, tz);
  if (!d0?.isValid() || !d1?.isValid() || !d1.isAfter(d0)) {
    return emptyPair;
  }
  if (d1.diff(d0, "minute") < MIN_PHASE_MIN * 2) {
    return emptyPair;
  }

  // Earliest midnight after "start + 3h" is the best shot for phase 2; try the next day once if
  // Meta’s 15m UTC snap lands the junction a hair too early for phase 1.
  const minPhase1End = d0.add(MIN_PHASE_MIN, "minute");
  let candidateDay = nextLocalMidnightOnOrAfter(minPhase1End, tz);

  for (let bump = 0; bump < 2; bump++) {
    if (!candidateDay.isBefore(d1)) {
      return emptyPair;
    }
    // Same wire as {@link snapPickerDayjsToScheduledPhaseDayStartWallMinute} on the DatePicker
    // so day-only phase rows show/stored midnight-aligned junctions.
    const junction = snapPickerDayjsToScheduledPhaseDayStartWallMinute(
      candidateDay,
      tz,
      d0
    );
    const dMid = wallMinuteToDayjsInEventZone(junction, tz);
    if (!dMid?.isValid()) {
      candidateDay = candidateDay.add(1, "day");
      continue;
    }
    if (d1.diff(dMid, "minute") < MIN_PHASE_MIN) {
      return emptyPair;
    }
    if (bothPhasesMeetMin(d0, dMid, d1)) {
      return [
        { time_start: startWall, time_end: junction },
        { time_start: junction, time_end: endWall },
      ];
    }
    candidateDay = candidateDay.add(1, "day");
  }

  return emptyPair;
}
